import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { supabase } from '../config/supabase';
import AppSkeleton from '../components/ui/AppSkeleton';

interface AuthContextType {
  user: any;
  businessId: string | null;
  setBusinessId: (businessId: string | null) => void;
  signUp: (email: string, password: string, name: string) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  createBusinessProfile: (businessData: any) => Promise<any>;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Cache utilities for business_id
const BUSINESS_CACHE_KEY = 'repairdata_business_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface BusinessCache {
  businessId: string | null;
  userId: string;
  timestamp: number;
}

const getCachedBusinessId = (userId: string): string | null => {
  try {
    const cached = localStorage.getItem(BUSINESS_CACHE_KEY);
    if (!cached) return null;

    const cache: BusinessCache = JSON.parse(cached);
    if (cache.userId !== userId) return null;

    // Check if cache is expired
    if (Date.now() - cache.timestamp > CACHE_DURATION) {
      localStorage.removeItem(BUSINESS_CACHE_KEY);
      return null;
    }

    return cache.businessId;
  } catch (error) {
    console.warn('Error reading business cache:', error);
    return null;
  }
};

const setCachedBusinessId = (userId: string, businessId: string | null) => {
  try {
    const cache: BusinessCache = {
      businessId,
      userId,
      timestamp: Date.now(),
    };
    localStorage.setItem(BUSINESS_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('Error setting business cache:', error);
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastCheckedUserId, setLastCheckedUserId] = useState<string | null>(null);
  const [isQuerying, setIsQuerying] = useState(false);

  // Function to check business_id with caching
  const checkBusinessId = async (userId: string, mounted: boolean): Promise<string | null> => {
    // Don't query if already checking or already checked this user
    if (isQuerying || lastCheckedUserId === userId) {
      return businessId;
    }

    // First, try to get from cache
    const cachedBusinessId = getCachedBusinessId(userId);
    if (cachedBusinessId !== null) {
      if (mounted) {
        setBusinessId(cachedBusinessId);
        setLastCheckedUserId(userId);
        console.log('AuthContext: Using cached business_id:', cachedBusinessId);
      }
      return cachedBusinessId;
    }

    setIsQuerying(true);

    try {
      const userQueryPromise = supabase
        .from('users')
        .select('business_id')
        .eq('id', userId)
        .single();

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Query timeout')), 3000) // Reduced to 3 seconds
      );

      const result = await Promise.race([userQueryPromise, timeoutPromise]);
      const foundBusinessId = result.data?.business_id || null;

      if (mounted) {
        setBusinessId(foundBusinessId);
        setLastCheckedUserId(userId);
        // Cache the result
        setCachedBusinessId(userId, foundBusinessId);
        if (foundBusinessId) {
          console.log('AuthContext: User has business:', foundBusinessId);
        }
      }

      return foundBusinessId;
    } catch (error) {
      console.warn('AuthContext: Business check failed, keeping existing businessId if available');

      // Only set to null if we don't already have a businessId (avoid overwriting existing data)
      if (mounted && !businessId) {
        setBusinessId(null);
        setLastCheckedUserId(userId);
        // Cache null result to avoid repeated queries
        setCachedBusinessId(userId, null);
      }

      // Return existing businessId if available, otherwise null
      return businessId;
    } finally {
      if (mounted) {
        setIsQuerying(false);
      }
    }
  };

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        console.log('AuthContext: Starting initialization');
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Auth initialization error:', error);
          if (mounted) {
            setUser(null);
            setBusinessId(null);
            setLoading(false);
          }
          return;
        }

        if (!session?.user) {
          console.log('AuthContext: No session found');
          if (mounted) {
            setUser(null);
            setBusinessId(null);
            setLoading(false);
          }
          return;
        }

        console.log('AuthContext: Session found, user:', session.user.id);
        if (mounted) {
          setUser(session.user);
          // Check business_id with caching (should be fast due to cache)
          const foundBusinessId = await checkBusinessId(session.user.id, mounted);
          console.log('AuthContext: Business check result:', foundBusinessId);
          setLoading(false);
        }
      } catch (error) {
        console.error('AuthContext: Initialization exception:', error);
        if (mounted) {
          setUser(null);
          setBusinessId(null);
          setLoading(false);
        }
      }
    };

    // Fallback: Force loading to complete after 3 seconds (matches query timeout)
    const fallbackTimer = setTimeout(() => {
      if (mounted && loading) {
        console.warn('AuthContext: Fallback timeout - forcing loading to complete');
        setUser(null);
        setBusinessId(null);
        setLoading(false);
      }
    }, 3000);

    initializeAuth();

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (!session?.user) {
        setUser(null);
        setBusinessId(null);
        setLoading(false);
        // Clear cache on logout
        localStorage.removeItem(BUSINESS_CACHE_KEY);
        return;
      }

      if (mounted) {
        setUser(session.user);
        // Always check business_id on auth state change to ensure consistency
        await checkBusinessId(session.user.id, mounted);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      authListener?.subscription.unsubscribe();
    };
  }, [businessId]);


  const signUp = async (email: string, password: string, name: string) => {
    // This is now handled directly in Signup component
    // Keeping for backward compatibility
    return supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name }
      }
    });
  };

  const signIn = async (email: string, password: string) => {
    const result = await supabase.auth.signInWithPassword({
      email,
      password
    });

    // If sign in is successful, the auth state change will handle navigation
    // based on whether the user has a business profile

    return result;
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error during sign out:', error);
        throw error;
      }
      setUser(null);
      setBusinessId(null);
    } catch (error) {
      console.error('Exception during sign out:', error);
      throw error;
    }
  };

  const createBusinessProfile = async (businessData: any) => {
    try {
      const { data: { user }, error: userFetchError } = await supabase.auth.getUser();

      if (userFetchError || !user) {
        throw new Error('No authenticated user found');
      }

      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .insert([
          {
            name: businessData.name,
            address: businessData.address,
            phone: businessData.phone,
            logo_url: businessData.logo_url,
          },
        ])
        .select()
        .single();

      if (businessError || !business) {
        throw new Error(`Failed to create business: ${businessError?.message || 'Unknown error'}`);
      }

      // Update user record with business association
      const { error: userError } = await supabase
        .from('users')
        .update({ business_id: business.id })
        .eq('id', user.id);

      if (userError) {
        // Clean up the business record if user update fails
        await supabase.from('businesses').delete().eq('id', business.id);
        throw new Error(`Failed to associate user with business: ${userError.message}`);
      }

      // Update local state and cache
      setBusinessId(business.id);
      setCachedBusinessId(user.id, business.id);

      return { business, error: null };
    } catch (error) {
      console.error('Error creating business profile:', error);
      return { business: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  };

  const setBusinessIdState = (newBusinessId: string | null) => {
    setBusinessId(newBusinessId);
    // Update cache if user is available
    if (user) {
      setCachedBusinessId(user.id, newBusinessId);
    }
  };

  const value = {
    user,
    businessId,
    setBusinessId: setBusinessIdState,
    signUp,
    signIn,
    signOut,
    createBusinessProfile,
    loading,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <AppSkeleton />
      ) : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};